import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Injectable, inject } from '@angular/core';

import { User } from '../models/user';
import { ApiService } from './api.service';

export type SubStatus =
  | 'inProcess'
  | 'documents'
  | 'offering'
  | 'offeringRejected'
  | 'Account_rejected'
  | 'pep'
  | 'kws'
  | 'finapiStop';

export interface MaintinanceData {
  from: Date;
  to: Date;
  description: string;
  sendEmails: boolean;
  creator: User;
}

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private readonly api = inject(ApiService);
  private readonly institutionId$ = new BehaviorSubject<string | null>(null);

  getUserList(userRole: string, lang: string): Observable<User[]> {
    return this.api.get<User[]>('user/list', { userRole, lang });
  }

  getUserById(id: string): Observable<User> {
    return this.api.get<User>(`user/byid/${id}`).pipe(map((resUser) => new User(resUser)));
  }

  deleteUserById(id: string): Observable<unknown> {
    return this.api.delete(`user/deleteUserById/${id}`);
  }

  resetUser(id: string): Observable<unknown> {
    return this.api.post(`user/reset-user/${id}`);
  }

  resetUserQuestions(id: string): Observable<unknown> {
    return this.api.post(`user/reset-questions/${id}`);
  }

  unblockUserById(userId: string): Observable<unknown> {
    return this.api.put(`user/unblock/${userId}`);
  }

  updateSubStatus(userId: string, substatus: SubStatus): Observable<unknown> {
    return this.api.put(`user/substatus/${userId}`, { substatus });
  }

  changeRoleUser(userId: string, userRole: string, user: User): Observable<unknown> {
    return this.api.post(`user/change-rol/${userId}`, { rol: userRole, user });
  }

  saveCustomInfo(userId: string, customInfo: string): Observable<unknown> {
    return this.api.post(`user/${userId}/custom-info`, { customInfo });
  }

  setPriveUser(userId: string): Observable<unknown> {
    return this.api.post(`user/prive-send/${userId}`);
  }

  importUser(userJson: string): Observable<string> {
    return this.api.postText('user/importuser/', userJson, 'application/json');
  }

  sendAbweichungEmail(userId: string, customerMessage: string): Observable<unknown> {
    return this.api.post('user/abweichungmelden', { userId, customerMessage });
  }

  sendPepEmail(userId: string): Observable<unknown> {
    return this.api.post('user/pepmeldung', { userId });
  }

  getDocuments(fileId: string): Observable<Blob> {
    return this.api.getBlob(`documents/getDocuments/${fileId}`);
  }

  getUserZipData(userId: string): Observable<Blob> {
    return this.api.getBlob(`user/data-pack/${userId}`);
  }

  setMaintinance(data: MaintinanceData): Observable<unknown> {
    return this.api.post('maintinance', data);
  }

  isServerInMaintinance(): Observable<unknown> {
    return this.api.get('maintinance');
  }

  setInstitutionId(id: string): void {
    this.institutionId$.next(id);
  }

  getInstitutionId$(): Observable<string | null> {
    return this.institutionId$.asObservable();
  }
}
